import React, { FC, useMemo } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import {
  PostsQuery,
  ReviewsQuery,
  SupplierByIdQuery,
  SupplierByIdWithVideoThumbnailQuery,
  SuppliersQuery,
  ToursQuery,
} from "@graphql/types";
import { CACHE_TIME, SITE_URL } from "@constants";
import { getMetaDataDescription } from "@helpers/getMetaDataDescription";
import { ShareButton } from "@components/ui/ShareButton";
import LoadingPage from "@components/pages/LoadingPage";
import Head from "@components/common/Head";
import { Alert } from "@components/ui/Alert";
import { Container } from "@components/ui/Container";
import { Breadcrumb } from "@components/ui/Breadcrumb";
import BreadcrumbMicroFormat from "@components/common/MicroFormat/BreadcrumbMicroFormat";
import { Sidebar } from "@components/pages/SupplierPage/Sidebar";
import { Title } from "@components/ui/Title";
import { ShowMore } from "@components/ui/ShowMore";
import { Paragraph } from "@components/ui/Paragraph";
import { Tours } from "@components/pages/SupplierPage/Tours";
import Reviews from "@components/common/Reviews";
import { Posts } from "@components/pages/SupplierPage/Posts";
import "react-toastify/dist/ReactToastify.css";
import ProductMicroFormat from "@components/common/MicroFormat/ProductMicroFormat";
import {
  getPostsVariables,
  getReviewsVariables,
  getToursVariables,
  loader_SupplierById,
} from "@components/pages/SupplierPage/loader";
import { useRouter } from "next/router";
import { PreviewTag } from "@components/common/PreviewTag";
import SuppliersBlock from "@components/common/SupplierBlock";
import { getNodes } from "@helpers/getNodes";
import { searchURL } from "@helpers/searchURL";
import { ButtonLink } from "@components/ui/Button";
import GalleryNew, {
  IGalleryItemProps,
  GalleryProps,
} from "@components/ui/GalleryNew";
import { t, Trans } from "@helpers/translate";
import { SEOSupplierLinks } from "@components/common/SEO/SEOSupplierLinks";

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const supplierId = Number(params?.id);
  const res = await loader_SupplierById({ id: supplierId });

  if ("props" in res) {
    return {
      ...res,
      revalidate: CACHE_TIME, //  1 hour cache
    };
  }

  return res;
};

type Props = {
  initialData?: {
    supplier: SupplierByIdWithVideoThumbnailQuery;
    tours: ToursQuery;
    posts: PostsQuery;
    reviews: ReviewsQuery;
    suppliers: SuppliersQuery;
  };
};

const Page: FC<Props> = ({ initialData }) => {
  const supplier = initialData?.supplier?.supplierById;
  const router = useRouter();

  if (!supplier) {
    return <LoadingPage />;
  }

  const galleryItems = useMemo(() => {
    const items: GalleryProps = {
      gallery: [] as IGalleryItemProps[],
      galleryMD: [] as IGalleryItemProps[],
    };

    if (supplier.video) {
      items.gallery.push({
        type: "video",
        src: supplier.video,
        caption: undefined,
        thumbnail: supplier.videoThumbnail || supplier.picture?.cover || "",
      });
      items.galleryMD.push({
        type: "video",
        src: supplier.video,
        caption: undefined,
        thumbnail: supplier.videoThumbnail || supplier.picture?.cover || "",
      });
    }
    supplier.gallery?.forEach((item) => {
      if (item.src) {
        items.gallery.push({
          type: "image",
          src: item.src,
          alt: `${supplier.city?.name} Private Tour - ${item.caption}`,
          caption: item.caption || "",
        });
      }
    });
    supplier.galleryMD?.forEach((item) => {
      if (item.src) {
        items.galleryMD.push({
          type: "image",
          src: item.src,
          alt: `${supplier.city?.name} Private Tour - ${item.caption}`,
          caption: item.caption || "",
        });
      }
    });
    return items;
  }, [supplier]);

  const serpProductImages = useMemo(() => {
    const images: string[] = [];

    // Take cover picture
    if (supplier.picture) {
      images.push(supplier.picture?.serpImage || supplier.picture?.url);
    }

    // Take gallery images
    supplier.gallery?.forEach((galleryItem) => {
      images.push(galleryItem?.serpImage || galleryItem?.src);
    });
    return images;
  }, [supplier]);

  const citiesNames = supplier.cities
    .map((city) => city.name)
    .splice(0, 4)
    .join(", ");
  const countryName = supplier.country?.name;
  const supplierName = supplier.displayName;

  const breadcrumbData = useMemo(() => {
    const steps = [
      {
        text: supplier.country?.name || "",
        href: supplier.country?.url || "",
      },
      {
        text: supplier?.city?.name || "",
        href: supplier?.city?.url || "",
      },
      { text: "Guides", href: `${supplier?.city?.url}/guides` },
      {
        text: supplier.displayName,
        href: supplier.url,
      },
    ];
    return steps;
  }, []);

  return (
    <>
      <Head
        title={t({
          id: "supplier.seo.title",
          message: `${supplierName} - Local Tour Guide in ${citiesNames}...`,
        })}
        description={getMetaDataDescription(
          t({
            id: "supplier.seo.description",
            message: `Plan your trip to ${citiesNames}... with ${supplierName} Private Tour Guide, Save your time!, Request your ${countryName} Itinerary, and Book Online!.`,
          })
        )}
        openGraph={{
          images: [{ url: supplier?.picture?.cover || "" }],
        }}
        noindex={supplier.isBan}
        nofollow={supplier.isBan}
      />

      <ProductMicroFormat
        productID={`${supplier.displayName}`}
        name={`${supplierName} - Local Tour Guide in ${citiesNames}..`}
        description={t({
          id: "supplier.seo.description",
          message: `Plan your trip to ${citiesNames}... with ${supplierName} Private Tour Guide, Save your time!, Request your ${countryName} Itinerary, and Book Online!.`,
        })}
        image={serpProductImages}
        contentUrl={supplier.video || undefined}
        embedUrl={supplier.video || undefined}
        reviewCount={supplier?.stats.reviewsCount}
      />

      {router.pathname.includes("preview") &&
        supplier &&
        supplier?.isOnline == false && <PreviewTag />}

      {supplier.isBan && (
        <Alert
          variant="warning"
          className="p-4 mb-10"
          centered={true}
          showIcon={false}
        >
          <Trans id="tour.supplier.status.ban">
            This guide is no longer active on GoWithGuide. We apologize for the
            inconvenience, but please check out other guides and tours in this
            area.
          </Trans>
        </Alert>
      )}

      <Container className="pt-2">
        <div className="flex flex-row relative">
          <div className="flex w-9/12 md:w-10/12">
            <div className="flex overflow-x-auto w-full items-center">
              <Breadcrumb items={breadcrumbData} noWrap />
            </div>

            <BreadcrumbMicroFormat items={breadcrumbData} />
          </div>
          <div className="flex ml-auto">
            <ShareButton
              type="supplier"
              country={supplier.country?.name || ""}
              displayName={supplier.displayName}
              url={SITE_URL + supplier.url}
            />
          </div>
        </div>
        <div className="flex flex-col mt-2">
          {supplier.gallery && (
            <GalleryNew
              gallery={galleryItems.gallery}
              galleryMD={galleryItems.galleryMD}
            />
          )}
          <div className="flex flex-wrap mt-8">
            <div className="w-full lg:w-2/3 lg:pr-8">
              <Sidebar
                supplier={supplier as SupplierByIdQuery["supplierById"]}
                variant="default"
                showFilters={true}
                showScarcity={true}
                showVerifiedData
              />

              <ShowMore className="mt-2 mb-20">
                <Title as="h1" size="md" divider>
                  {supplier.isCar &&
                    t({
                      id: "supplier.car.block.about.title",
                      message: `About ${supplierName}`,
                    })}

                  {!supplier.isCar &&
                    t({
                      id: "supplier.block.about.title",
                      message: `About Tour Guide ${supplierName}`,
                    })}
                </Title>

                <Paragraph>{supplier.biography}</Paragraph>
              </ShowMore>

              {supplier.important && supplier.important.trim().length > 0 && (
                <div className="my-10">
                  <Title as="h2" size="md" divider>
                    <Trans id="supplier.block.important">Important</Trans>
                  </Title>

                  <Paragraph>{supplier.important.trim()}</Paragraph>
                </div>
              )}

              <div className="mobile-negative-margin">
                <Tours
                  tours={initialData.tours}
                  badgesAvailable={
                    supplier.supplierTourBadges.map((tb) => ({
                      id: tb.id,
                      name: tb.name,
                      picture_url: tb.picture?.url || "missing",
                      hex_color_code: tb.hex_color_code || "red",
                      available: true,
                    })) || []
                  }
                  filters={getToursVariables(supplier.id)}
                />
              </div>
              <div id="reviews">
                <Reviews
                  type="supplier"
                  initialData={initialData?.reviews}
                  filters={getReviewsVariables(supplier.id)}
                  title={t({
                    id: "supplier.block.review.title",
                    message: "Reviews",
                  })}
                />
              </div>
              <div className="mobile-negative-margin">
                <Posts
                  initialData={initialData?.posts}
                  filters={getPostsVariables(supplier.id)}
                />
              </div>
            </div>

            <div className="w-1/3 hidden lg:block">
              <Sidebar
                supplier={supplier as SupplierByIdQuery["supplierById"]}
                showFilters={true}
                showScarcity={true}
              />
            </div>
          </div>
          <div className="mobile-negative-margin">
            {initialData?.suppliers?.suppliers.edges.length >= 1 && (
              <SuppliersBlock
                title={t({
                  id: "supplier.block.others-popular-guides.title",
                  message: `Other Popular Tour Guides in ${supplier.city?.name}`,
                })}
                data={getNodes(initialData.suppliers.suppliers)}
                className="mt-8 md:mt-16"
                enableSwipeOnMobile={true}
              >
                {initialData?.suppliers?.suppliers.pageInfo.hasNextPage &&
                  supplier.city && (
                    <ButtonLink
                      href={searchURL({
                        t: "guides",
                        city: supplier.city.slug,
                      })}
                      variant="default-outline"
                      title={t({
                        id: "blog.block.suppliers.gowithguide.view-all",
                        message: "GoWithGuide - View All",
                      })}
                      className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
                    >
                      <Trans id="blog.block.suppliers.button.generic">
                        View All {supplier.city.name} Tour Guides
                      </Trans>
                    </ButtonLink>
                  )}
              </SuppliersBlock>
            )}
          </div>
          <SEOSupplierLinks cities={supplier.cities} />
        </div>
      </Container>
    </>
  );
};

export default Page;
